import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import ConfirmModal from "../../components/ConfirmModal";
import ErrorModal from "../../components/ErrorModal";
import TimeInput from "./TimeInput";
import {
  MONTH_NAMES,
  apiErrorMessage,
  defaultBreak,
  formatDate,
  formatSignedMinutes,
  formatHoursMinutes,
  isoWeekKey,
  todayIso,
  trimSeconds,
  weekdayName,
} from "../../utils/timesheets";

const inputClass =
  "w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelClass = "block text-xs font-medium text-gray-600 mb-1";

/** (i)-icoontje rechts in de codekolom; bij hover of focus verschijnt de opmerking. */
function NoteIcon({ note }) {
  return (
    <span className="group relative ml-auto inline-flex shrink-0" tabIndex={0} role="img" aria-label={`Opmerking: ${note}`}>
      <svg className="h-4 w-4 text-gray-400 group-hover:text-blue-600 group-focus:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" strokeWidth="2" />
        <path strokeLinecap="round" strokeWidth="2" d="M12 11v5" />
        <circle cx="12" cy="8" r="0.6" fill="currentColor" strokeWidth="1" />
      </svg>
      <span
        role="tooltip"
        className="pointer-events-none absolute right-0 top-full z-30 mt-1 hidden w-64 whitespace-normal rounded border border-gray-200 bg-white p-2 text-left text-xs font-normal text-gray-700 shadow-lg group-hover:block group-focus:block"
      >
        {note}
      </span>
    </span>
  );
}

/**
 * Interne urenstaat voor één maand: per dag registraties met een code, basisuren, overuren en saldi.
 * De server berekent alles; dit scherm toont en voert in.
 */
export default function InternalMonth({ memberId, view, status, reloadKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null); // datum waarvoor het formulier openstaat
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState("");
  const [confirm, setConfirm] = useState(null);
  const tempCounter = useRef(0);

  const query = `year=${view.year}&month=${view.month}${memberId ? `&member_id=${memberId}` : ""}`;

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(`/timesheets/internal/month?${query}`));
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const editable = status === "open";
  const codes = data?.codes || [];
  const codeInfo = useMemo(() => Object.fromEntries(codes.map((c) => [c.code, c])), [codes]);
  const lineFor = (iso) =>
    (data?.lines || []).filter((l) => l.start_date <= iso && (!l.end_date || l.end_date >= iso)).pop();

  const openForm = (iso, entry) => {
    setFormError("");
    setOpen(iso);
    if (entry) {
      setForm({
        id: entry.id, code: entry.code, start: trimSeconds(entry.start_time), end: trimSeconds(entry.end_time),
        breakMinutes: entry.break_minutes === null ? "" : String(entry.break_minutes),
        absenceMinutes: entry.absence_minutes ? String(entry.absence_minutes) : "", note: entry.note || "",
      });
    } else {
      setForm({ code: "100", start: "", end: "", breakMinutes: "", absenceMinutes: "", note: "", touchedBreak: false });
    }
  };

  const effect = codeInfo[form.code]?.effect;

  // Escape sluit het registratievenster
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // pauze voorinvullen volgens de interne lijn, zolang ze niet zelf is aangepast
  useEffect(() => {
    if (!open || effect !== "work" || form.touchedBreak || form.id) return;
    const suggestion = defaultBreak(lineFor(open), form.start, form.end);
    setForm((cur) => (cur.breakMinutes === suggestion ? cur : { ...cur, breakMinutes: suggestion }));
  }, [open, effect, form.start, form.end, form.touchedBreak, form.id, data]);

  // De rij verschijnt meteen; daarna haalt de maand de berekende saldi op. Bij een fout wordt alles hersteld.
  const submitForm = (e) => {
    e.preventDefault();
    setFormError("");
    const line = lineFor(open);
    if (!line) return setFormError("Er is geen lopende interne lijn op deze datum.");
    if (effect === "work" && (!form.start || !form.end)) return setFormError("Geef start- en einduur in.");
    const sentForm = form;
    const sentDate = open;
    const payload = {
      contract_line_id: line.id,
      code: form.code,
      start_time: effect === "work" ? form.start : null,
      end_time: effect === "work" ? form.end : null,
      break_minutes: effect === "work" && form.breakMinutes !== "" ? Number(form.breakMinutes) : null,
      absence_minutes: effect !== "work" && form.absenceMinutes !== "" ? Math.round(Number(form.absenceMinutes) * 60) : null,
      note: form.note || null,
    };
    tempCounter.current += 1;
    const entryId = form.id || `tmp-${tempCounter.current}`;
    const shown = {
      id: entryId, pending: true, code: payload.code, start_time: payload.start_time, end_time: payload.end_time,
      break_minutes: payload.break_minutes, absence_minutes: payload.absence_minutes, note: payload.note,
      contract_line_id: line.id,
    };
    setData((cur) => ({
      ...cur,
      days: cur.days.map((d) =>
        d.date !== sentDate ? d : {
          ...d,
          entries: form.id ? d.entries.map((en) => (en.id === form.id ? shown : en)) : [...d.entries, shown],
        }
      ),
    }));
    setOpen(null);

    const request = form.id
      ? apiFetch(`/timesheets/entries/${form.id}`, { method: "PATCH", body: JSON.stringify(payload) })
      : apiFetch("/timesheets/entries", {
          method: "POST",
          body: JSON.stringify({ ...payload, entry_type: "code", entry_date: sentDate, ...(memberId ? { member_id: memberId } : {}) }),
        });
    request
      .then(() => load())
      .catch(async (err) => {
        await load();
        setOpen(sentDate);
        setForm(sentForm);
        setFormError(apiErrorMessage(err));
      });
  };

  const removeEntry = (id) => {
    setConfirm(null);
    setData((cur) => ({ ...cur, days: cur.days.map((d) => ({ ...d, entries: d.entries.filter((en) => en.id !== id) })) }));
    apiFetch(`/timesheets/entries/${id}`, { method: "DELETE" })
      .then(() => load())
      .catch(async (err) => {
        await load();
        setError(apiErrorMessage(err));
      });
  };

  const monthLabel = `${MONTH_NAMES[view.month - 1]} ${view.year}`;
  const weekTotals = useMemo(() => {
    const totals = {};
    (data?.days || []).forEach((d) => {
      const key = isoWeekKey(d.date);
      totals[key] = totals[key] || { worked: 0, overtime: 0 };
      totals[key].worked += d.worked;
      totals[key].overtime += d.overtime;
    });
    return totals;
  }, [data]);

  if (!data) return <div className="py-10 text-center text-gray-400">Laden...</div>;
  if (!data.has_line) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        Er is geen interne contractlijn (weekrooster) voor dit personeelslid. Maak er eerst een aan in het beheer.
      </div>
    );
  }

  const rows = [];
  data.days.forEach((d, index) => {
    const weekend = ["Zaterdag", "Zondag"].includes(weekdayName(d.date));
    const muted = weekend || d.holiday;
    rows.push(
      <tr key={d.date} className={`border-b border-gray-50 ${muted ? "bg-gray-50 text-gray-500" : ""} ${d.date === todayIso() ? "ring-1 ring-inset ring-blue-300" : ""}`}>
        <td className="px-3 py-1.5 whitespace-nowrap text-right">
          <span className="mr-2 text-gray-500">{weekdayName(d.date).slice(0, 2).toLowerCase()}</span>{formatDate(d.date)}
        </td>
        <td className="px-3 py-1.5">
          {d.entries.length === 0 && d.holiday && <span className="text-gray-500">220 · Feestdag</span>}
          {d.entries.map((e) => (
            <div key={e.id} className="flex items-center gap-2">
              <span className="font-medium text-gray-800">{e.code}</span>
              <span className="text-gray-500">{codeInfo[e.code]?.description}</span>
              {e.absence_minutes ? <span className="text-xs text-gray-500">({formatHoursMinutes(e.absence_minutes)})</span> : null}
              {e.pending && <span className="text-xs text-gray-400">opslaan…</span>}
              {editable && !e.pending && (
                <>
                  <button onClick={() => openForm(d.date, e)} className="text-xs text-blue-600 hover:text-blue-800">Bewerken</button>
                  <button onClick={() => setConfirm({ type: "delete", id: e.id, date: d.date })} className="text-xs text-red-500 hover:text-red-700">Verwijderen</button>
                </>
              )}
              {e.note && <NoteIcon note={e.note} />}
            </div>
          ))}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums">
          {d.entries.map((e) => <div key={e.id}>{trimSeconds(e.start_time)}</div>)}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums">
          {d.entries.map((e) => <div key={e.id}>{trimSeconds(e.end_time)}</div>)}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">
          {d.entries.map((e) => <div key={e.id}>{e.break_minutes ? formatHoursMinutes(e.break_minutes) : ""}</div>)}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums">{d.worked ? formatHoursMinutes(d.worked) : ""}</td>
        <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{d.basis ? formatHoursMinutes(d.effective_basis) : ""}</td>
        <td className={`px-3 py-1.5 text-right tabular-nums ${d.overtime < 0 ? "text-red-600" : ""}`}>{d.overtime ? formatSignedMinutes(d.overtime) : ""}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{formatSignedMinutes(d.overtime_balance, true, false)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{formatSignedMinutes(d.vacation_balance, true, false)}</td>
        <td className="px-3 py-1.5 text-right whitespace-nowrap">
          {editable && (
            <button onClick={() => openForm(d.date)} className="text-xs text-blue-600 hover:text-blue-800">+ Registratie</button>
          )}
        </td>
      </tr>
    );
    const next = data.days[index + 1];
    if (!next || isoWeekKey(next.date) !== isoWeekKey(d.date)) {
      const total = weekTotals[isoWeekKey(d.date)];
      rows.push(
        <tr key={`w-${d.date}`} className="bg-blue-50 text-xs font-medium text-blue-900">
          <td className="px-3 py-1" colSpan={5}>Weektotaal</td>
          <td className="px-3 py-1 text-right tabular-nums">{formatHoursMinutes(total.worked)}</td>
          <td></td>
          <td className="px-3 py-1 text-right tabular-nums">{formatSignedMinutes(total.overtime, true)}</td>
          <td colSpan={3}></td>
        </tr>
      );
    }
  });

  return (
    <div>
      {error && <ErrorModal message={error} onClose={() => setError("")} />}
      {confirm?.type === "delete" && (
        <ConfirmModal
          title="Registratie verwijderen"
          message={`Registratie van ${formatDate(confirm.date)} verwijderen?`}
          confirmLabel="Verwijderen" danger
          onConfirm={() => removeEntry(confirm.id)} onCancel={() => setConfirm(null)}
        />
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-500">Saldo overuren</div>
          <div className="text-xl font-semibold tabular-nums">{data.balances ? formatSignedMinutes(data.balances.overtime, true, false) : "—"}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-500">Saldo vakantie (uren)</div>
          <div className="text-xl font-semibold tabular-nums">{data.balances ? formatSignedMinutes(data.balances.vacation, true, false) : "—"}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-500">Gewerkt in {monthLabel}</div>
          <div className="text-xl font-semibold tabular-nums">{formatHoursMinutes(data.totals.worked)}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-500">Overuren in {monthLabel}</div>
          <div className="text-xl font-semibold tabular-nums">{formatSignedMinutes(data.totals.overtime, true)}</div>
        </div>
      </div>
      {!data.balance_start && (
        <div className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Er is nog geen startsaldo ingesteld; de saldi starten daardoor op 0.
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black bg-opacity-50 p-4 overflow-y-auto">
          <form
            onSubmit={submitForm}
            role="dialog"
            aria-modal="true"
            aria-labelledby="registration-title"
            className="bg-white rounded-lg shadow-xl w-full max-w-lg my-16 p-6"
          >
            <h2 id="registration-title" className="text-lg font-bold text-gray-800 mb-4">
              {form.id ? "Registratie bewerken" : "Registratie toevoegen"}
              <span className="block text-sm font-normal text-gray-500">{weekdayName(open)} {formatDate(open)}</span>
            </h2>

            <div className="mb-4">
              <label className={labelClass} htmlFor="code">Code</label>
              <select id="code" autoFocus value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className={inputClass}>
                {codes.map((c) => (
                  <option key={c.code} value={c.code}>{c.code} · {c.description}</option>
                ))}
              </select>
            </div>

            {effect === "work" ? (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <TimeInput id="i-start" label="Start" value={form.start} onChange={(v) => setForm({ ...form, start: v })} />
                <TimeInput id="i-end" label="Einde" value={form.end} onChange={(v) => setForm({ ...form, end: v })} />
                <div>
                  <label className={labelClass} htmlFor="i-break">Pauze (min)</label>
                  <input id="i-break" type="number" min="0" step="5" value={form.breakMinutes}
                    onChange={(e) => setForm({ ...form, breakMinutes: e.target.value, touchedBreak: true })} className={inputClass} />
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <label className={labelClass} htmlFor="i-hours">Aantal uren (leeg = hele dag)</label>
                <input id="i-hours" type="number" min="0" step="0.25" value={form.absenceMinutes}
                  onChange={(e) => setForm({ ...form, absenceMinutes: e.target.value })} className={inputClass} />
              </div>
            )}

            <div className="mb-4">
              <label className={labelClass} htmlFor="i-note">Opmerking (optioneel)</label>
              <input id="i-note" type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputClass} />
            </div>

            {formError && <p className="mb-4 text-sm text-red-600" role="alert">{formError}</p>}

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(null)} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50">Annuleren</button>
              <button type="submit" className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700">
                {form.id ? "Opslaan" : "Toevoegen"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: 1100 }}>
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase">
              <th className="px-3 py-2 text-right">Datum</th>
              <th className="px-3 py-2 text-left">Code</th>
              <th className="px-3 py-2 text-right">Start</th>
              <th className="px-3 py-2 text-right">Einde</th>
              <th className="px-3 py-2 text-right">Pauze</th>
              <th className="px-3 py-2 text-right">Gewerkt</th>
              <th className="px-3 py-2 text-right">Basis</th>
              <th className="px-3 py-2 text-right">Overuren</th>
              <th className="px-3 py-2 text-right">Saldo overuren</th>
              <th className="px-3 py-2 text-right">Saldo vakantie</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>

    </div>
  );
}
